import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, Download, Eye, Calendar, X, Phone, Mail, Users, CreditCard, FileText } from "lucide-react";
import { toast } from "sonner";

const formatINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  confirmed: "bg-green-100 text-green-800",
  completed: "bg-blue-100 text-blue-800",
  cancelled: "bg-red-100 text-red-800",
  refunded: "bg-gray-100 text-gray-800",
  voucher_issued: "bg-purple-100 text-purple-800",
};
const STATUS_LABELS = {
  pending: "Awaiting Payment",
  confirmed: "Confirmed",
  completed: "Completed",
  cancelled: "Cancelled",
  refunded: "Refunded",
  voucher_issued: "Voucher Issued",
};
const PAY_COLORS = {
  paid: "bg-green-100 text-green-800",
  pending: "bg-yellow-100 text-yellow-800",
  partial: "bg-orange-100 text-orange-800",
  refunded: "bg-gray-100 text-gray-800",
};

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({
    q: "",
    status: "",
    booking_date_from: "",
    booking_date_to: "",
    travel_date_from: "",
    travel_date_to: "",
    product_title: "",
  });

  const buildParams = useCallback(() => {
    const p = {};
    if (filters.q.trim()) p.q = filters.q.trim();
    if (filters.status) p.status = filters.status;
    if (filters.booking_date_from) p.booking_date_from = filters.booking_date_from;
    if (filters.booking_date_to) p.booking_date_to = filters.booking_date_to;
    if (filters.travel_date_from) p.travel_date_from = filters.travel_date_from;
    if (filters.travel_date_to) p.travel_date_to = filters.travel_date_to;
    if (filters.product_title.trim()) p.product_title = filters.product_title.trim();
    return p;
  }, [filters]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/bookings", { params: buildParams() })
      .then((r) => setBookings(r.data))
      .catch(() => toast.error("Failed to load bookings"))
      .finally(() => setLoading(false));
  }, [buildParams]);

  useEffect(() => { load(); }, []);

  const handleSearch = () => load();

  const handleKeyDown = (e) => { if (e.key === "Enter") load(); };

  const clearFilters = () => {
    setFilters({ q: "", status: "", booking_date_from: "", booking_date_to: "", travel_date_from: "", travel_date_to: "", product_title: "" });
    setTimeout(() => {
      setLoading(true);
      api.get("/admin/bookings").then((r) => setBookings(r.data)).finally(() => setLoading(false));
    }, 50);
  };

  const downloadExcel = () => {
    const params = new URLSearchParams(buildParams());
    const url = `${api.defaults.baseURL}/admin/bookings-export?${params.toString()}`;
    const token = localStorage.getItem("token");
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `bookings_export_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        toast.success("Download started");
      })
      .catch(() => toast.error("Export failed"));
  };

  const viewDetail = async (b) => {
    try {
      const { data } = await api.get(`/admin/bookings/${b.id}`);
      setSelected(data);
    } catch {
      toast.error("Failed to load booking details");
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/admin/bookings/${id}/status`, { status });
      toast.success(`Status updated to ${status}`);
      setBookings((prev) => prev.map((b) => b.id === id ? { ...b, status } : b));
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch {
      toast.error("Status update failed");
    }
  };

  const hasActiveFilters = filters.q || filters.status || filters.booking_date_from || filters.booking_date_to || filters.travel_date_from || filters.travel_date_to || filters.product_title;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-bookings-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl">Bookings</h1>
          <p className="text-sm text-muted-foreground">{loading ? "Loading..." : `${bookings.length} bookings found`}</p>
        </div>
        <Button onClick={downloadExcel} variant="outline" data-testid="bookings-export" disabled={bookings.length === 0}>
          <Download className="w-4 h-4 mr-2" /> Export CSV
        </Button>
      </div>

      {/* Search & Filters */}
      <div className="bg-white border border-border rounded-2xl p-4 mb-6 space-y-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-xs">Search (Name / Phone / Ticket #)</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                onKeyDown={handleKeyDown}
                placeholder="Mobile number, ticket #, or name..."
                className="pl-9"
                data-testid="bookings-search"
              />
            </div>
          </div>
          <div className="w-40">
            <Label className="text-xs">Status</Label>
            <Select value={filters.status || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "all" ? "" : v }))}>
              <SelectTrigger data-testid="bookings-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Awaiting Payment</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="voucher_issued">Voucher Issued</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-48">
            <Label className="text-xs">Tour / Product</Label>
            <Input
              value={filters.product_title}
              onChange={(e) => setFilters((f) => ({ ...f, product_title: e.target.value }))}
              onKeyDown={handleKeyDown}
              placeholder="Tour name..."
              data-testid="bookings-filter-product"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <Label className="text-xs">Booking Date From</Label>
            <Input type="date" value={filters.booking_date_from} onChange={(e) => setFilters((f) => ({ ...f, booking_date_from: e.target.value }))} data-testid="bookings-filter-bd-from" />
          </div>
          <div>
            <Label className="text-xs">Booking Date To</Label>
            <Input type="date" value={filters.booking_date_to} onChange={(e) => setFilters((f) => ({ ...f, booking_date_to: e.target.value }))} data-testid="bookings-filter-bd-to" />
          </div>
          <div>
            <Label className="text-xs">Travel Date From</Label>
            <Input type="date" value={filters.travel_date_from} onChange={(e) => setFilters((f) => ({ ...f, travel_date_from: e.target.value }))} data-testid="bookings-filter-td-from" />
          </div>
          <div>
            <Label className="text-xs">Travel Date To</Label>
            <Input type="date" value={filters.travel_date_to} onChange={(e) => setFilters((f) => ({ ...f, travel_date_to: e.target.value }))} data-testid="bookings-filter-td-to" />
          </div>
          <Button onClick={handleSearch} className="bg-teal hover:bg-teal-dark text-white" data-testid="bookings-apply-filters">
            <Search className="w-4 h-4 mr-1" /> Search
          </Button>
          {hasActiveFilters && (
            <Button onClick={clearFilters} variant="ghost" size="sm" data-testid="bookings-clear-filters">
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          )}
        </div>
      </div>

      {/* Bookings Table */}
      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ticket #</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Travel Date</TableHead>
                <TableHead>Pax</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>{Array.from({ length: 9 }).map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>
                ))
              ) : bookings.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-12">No bookings match your search.</TableCell></TableRow>
              ) : (
                bookings.map((b) => (
                  <TableRow key={b.id} className="cursor-pointer hover:bg-muted/30" onClick={() => viewDetail(b)} data-testid={`booking-row-${b.reference_no}`}>
                    <TableCell className="font-mono text-xs font-semibold">{b.reference_no}</TableCell>
                    <TableCell>
                      <div className="text-sm font-medium">{b.contact_name}</div>
                      <div className="text-xs text-muted-foreground">{b.contact_phone}</div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm max-w-[200px] truncate">{b.product_title}</div>
                      <div className="text-xs text-muted-foreground capitalize">{b.product_category}</div>
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{b.travel_date || "-"}</TableCell>
                    <TableCell className="text-sm">{(b.adults || b.num_adults || 0) + (b.children || b.num_children || 0)}</TableCell>
                    <TableCell className="font-semibold text-saffron whitespace-nowrap">{formatINR(b.total_amount)}</TableCell>
                    <TableCell><Badge className={`${PAY_COLORS[b.payment_status] || PAY_COLORS.pending} text-[10px]`}>{b.payment_status}</Badge></TableCell>
                    <TableCell><Badge className={`${STATUS_COLORS[b.status] || STATUS_COLORS.pending} text-[10px]`}>{STATUS_LABELS[b.status] || b.status}</Badge></TableCell>
                    <TableCell>
                      <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); viewDetail(b); }} data-testid={`view-${b.reference_no}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Booking Detail Modal */}
      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-teal" />
                  Booking #{selected.reference_no}
                  <Badge className={`${STATUS_COLORS[selected.status] || ""} ml-auto`}>{STATUS_LABELS[selected.status] || selected.status}</Badge>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-5 mt-2">
                {/* Customer Info */}
                <div className="bg-muted/50 rounded-xl p-4">
                  <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-teal" /> Customer Details</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-muted-foreground">Name:</span> <strong>{selected.contact_name}</strong></div>
                    <div className="flex items-center gap-1"><Phone className="w-3 h-3 text-muted-foreground" /> {selected.contact_phone}</div>
                    <div className="flex items-center gap-1"><Mail className="w-3 h-3 text-muted-foreground" /> {selected.contact_email || "N/A"}</div>
                    <div><span className="text-muted-foreground">Booked on:</span> {selected.created_at?.slice(0, 10)}</div>
                  </div>
                </div>

                {/* Product & Travel Info */}
                <div className="bg-muted/50 rounded-xl p-4">
                  <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><Calendar className="w-4 h-4 text-teal" /> Travel Details</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="col-span-2"><span className="text-muted-foreground">Product:</span> <strong>{selected.product_title}</strong></div>
                    <div><span className="text-muted-foreground">Category:</span> <span className="capitalize">{selected.product_category}</span></div>
                    <div><span className="text-muted-foreground">Travel Date:</span> <strong>{selected.travel_date || "N/A"}</strong></div>
                    <div><span className="text-muted-foreground">Adults:</span> {selected.adults || selected.num_adults || 0}</div>
                    <div><span className="text-muted-foreground">Children:</span> {selected.children || selected.num_children || 0}</div>
                    {selected.num_vehicles > 0 && <div><span className="text-muted-foreground">Vehicles:</span> {selected.num_vehicles}</div>}
                    {selected.num_rooms > 0 && <div><span className="text-muted-foreground">Rooms:</span> {selected.num_rooms} ({selected.num_nights || 1} nights)</div>}
                    {selected.selected_vehicle && <div className="col-span-2"><span className="text-muted-foreground">Vehicle Type:</span> {selected.selected_vehicle}</div>}
                    {selected.selected_room && <div className="col-span-2"><span className="text-muted-foreground">Room Type:</span> {selected.selected_room}</div>}
                    {selected.pickup_location && <div className="col-span-2"><span className="text-muted-foreground">Pickup:</span> <strong>{selected.pickup_location}</strong></div>}
                    {selected.drop_location && <div className="col-span-2"><span className="text-muted-foreground">Drop:</span> {selected.drop_location}</div>}
                    {selected.special_requests && <div className="col-span-2"><span className="text-muted-foreground">Special Requests:</span> <em>{selected.special_requests}</em></div>}
                  </div>
                </div>

                {/* Payment Info */}
                <div className="bg-muted/50 rounded-xl p-4">
                  <h4 className="font-semibold text-sm mb-3 flex items-center gap-2"><CreditCard className="w-4 h-4 text-teal" /> Payment Details</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-muted-foreground">Base Amount:</span> {formatINR(selected.base_amount)}</div>
                    <div><span className="text-muted-foreground">GST:</span> {formatINR(selected.gst_amount)}</div>
                    <div><span className="text-muted-foreground">Service Charge:</span> {formatINR(selected.service_charge_amount)}</div>
                    <div><span className="text-muted-foreground font-semibold">Total:</span> <strong className="text-saffron">{formatINR(selected.total_amount)}</strong></div>
                    <div><span className="text-muted-foreground">Payment Status:</span> <Badge className={PAY_COLORS[selected.payment_status] || ""}>{selected.payment_status}</Badge></div>
                    {selected.razorpay_payment_id && <div><span className="text-muted-foreground">Razorpay ID:</span> <code className="text-xs bg-muted px-1 rounded">{selected.razorpay_payment_id}</code></div>}
                    {selected.razorpay_order_id && <div><span className="text-muted-foreground">Order ID:</span> <code className="text-xs bg-muted px-1 rounded">{selected.razorpay_order_id}</code></div>}
                    {selected.loyalty_points_redeemed > 0 && <div><span className="text-muted-foreground">Loyalty Points Used:</span> {selected.loyalty_points_redeemed}</div>}
                    {selected.coupon_code && <div><span className="text-muted-foreground">Coupon:</span> <code className="text-xs bg-muted px-1 rounded">{selected.coupon_code}</code></div>}
                  </div>
                </div>

                {/* Status Update */}
                <div className="flex items-center gap-3 pt-2 border-t">
                  <Label className="text-sm shrink-0">Update Status:</Label>
                  <Select value={selected.status} onValueChange={(v) => updateStatus(selected.id, v)}>
                    <SelectTrigger className="w-48" data-testid="detail-status-update"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["pending", "confirmed", "voucher_issued", "completed", "cancelled", "refunded"].map((s) => (
                        <SelectItem key={s} value={s}>{STATUS_LABELS[s] || s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
